import React, { PureComponent } from 'react'
import ReactDOM from 'react-dom'
import PropTypes from 'prop-types'
import { translate } from 'react-polyglot'
import { connect } from 'react-redux'
import classNames from 'classnames'
import Mousetrap from 'mousetrap'
import { isMobile } from 'react-device-detect'
import { Helmet } from 'react-helmet'

import DefaultLayout from 'layouts/Default'
import {
	ContentOverlay,
	PageHeader,
	Sidebar,
	PageFooterActions,
	Editor,
	Icon,
	Button,
	Text,
	Shortcut,
	OnboardingModal,
	ResponsibleMembersModal,
	FeedbackModal,
	SlugChangedModal,
} from 'components'
import { cleanTitle } from 'utils/common'
import { getShortcutString } from 'components/Shortcut'
import EntrySvg from 'assets/img/svg/entry.js'
import IcecreamSvg from 'assets/img/svg/icecream.js'

import AchievementModal from 'scenes/Achievements/AchievementModal'

class ArticlePage extends PureComponent {
	state = {
		sidebarCollapsed: false,
	}

	entry = React.createRef()

	componentDidMount() {
		const button = document.querySelector('.o-page__sidebar-toggle')

		this.setState({ sidebarCollapsed: this.isHidden(button) })

		Mousetrap(document.querySelector('body')).bind(
			getShortcutString('toggleSidebar'),
			this.onSidebarToggle
		)

		if (!isMobile) {
			this.setState({ sidebarCollapsed: true })
		}
	}

	UNSAFE_componentWillUpdate(nextProps) {
		const { id } = this.props

		if (id !== nextProps.id) {
			this.entry.current.parentNode.scrollTop = 0
		}
	}

	componentWillUnmount() {
		Mousetrap(document.querySelector('body')).unbind(
			getShortcutString('toggleSidebar'),
			this.onSidebarToggle
		)
	}

	onSidebarToggle = () => {
		const { sidebarCollapsed } = this.state

		this.setState({ sidebarCollapsed: !sidebarCollapsed })
	}

	isHidden = el => {
		const style = window.getComputedStyle(ReactDOM.findDOMNode(el))

		return style.display === 'none' || style.visibility === 'hidden'
	}

	renderSidebar = () => {
		const { sidebarCollapsed } = this.state

		return [
			<nav
				key="A"
				className={classNames('o-page__sidebar', {
					'is-visible': sidebarCollapsed,
				})}
			>
				<Sidebar
					sidebarCollapsed={sidebarCollapsed}
					onSidebarToggle={this.onSidebarToggle}
				/>
			</nav>,
			<div key="B" className="o-page__sidebar-toggle">
				<Button
					icon={<Icon name="sidebar-open" />}
					onClick={this.onSidebarToggle}
				/>
			</div>,
		]
	}

	renderError = status => {
		const { t } = this.props

		return (
			<ContentOverlay>
				<Text color="scheme" size="x-large" weight="light">
					{t('error_404_big_text', { status })}
				</Text>
				<h2>{t('error_404_title')}</h2>
				<Text>{t('error_404_text')}</Text>
				<IcecreamSvg />
			</ContentOverlay>
		)
	}

	renderContent = () => {
		const { status, articleCount, id, t } = this.props

		if (
			id !== null &&
			(status === 200 || (status === null && articleCount > 0))
		) {
			return <Editor />
		}

		if (articleCount !== null && articleCount === 0 && status === null) {
			return (
				<ContentOverlay>
					<Text color="scheme" size="x-large" weight="light">
						{t('article_empty_state_big_text')}
					</Text>
					<h2>{t('article_empty_state_title')}</h2>
					<Text>{t('article_empty_state_text')}</Text>
					<Text>
						{t('article_empty_state_cta')}
						<br />
						<Shortcut commandKey="createEntry" inline />
					</Text>
					<EntrySvg />
				</ContentOverlay>
			)
		}

		if (status !== null) {
			return this.renderError(status)
		}

		return <span />
	}

	render() {
		const { t, userId, name, title } = this.props

		return (
			<DefaultLayout
				header={<PageHeader />}
				sidebar={this.renderSidebar()}
				footer={
					<PageFooterActions className="o-page__footer-actions" />
				}
			>
				<Helmet>
					{title !== null && title !== '——––– ––––' && (
						<title>{cleanTitle(title) || t('no_title')}</title>
					)}
				</Helmet>
				<div className="o-entry" ref={this.entry}>
					{this.renderContent()}
				</div>
				<AchievementModal />
				<OnboardingModal name={name} />
				<ResponsibleMembersModal userId={userId} />
				<FeedbackModal />
				<SlugChangedModal />
			</DefaultLayout>
		)
	}
}

ArticlePage.propTypes = {
	userId: PropTypes.string,
	name: PropTypes.string,
	id: PropTypes.string,
	title: PropTypes.string,
	articleCount: PropTypes.number,
	status: PropTypes.number,
}

ArticlePage.defaultProps = {
	userId: null,
	name: '',
	id: null,
	title: null,
	articleCount: null,
	status: null,
}

const mapStateToProps = state => ({
	userId: state.auth.id,
	name: state.auth.firstName,
	id: state.articles.article.id,
	title: state.articles.article.editorTitle,
	articleCount: state.articles.list ? state.articles.list.size : null,
	status: state.articles.article.status,
})

export default connect(mapStateToProps, null)(translate()(ArticlePage))
